import * as Sentry from '@sentry/react';
import type { QueryCache } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  isMemoryDiagnosticsEnabled,
  memoryDiagnostics,
  MEMORY_DIAGNOSTICS_ENABLED_KEY,
  MEMORY_QUERY_FAMILIES,
  type MemoryQueryCounts,
  type MemoryQueryFamily,
  type MemoryResources,
} from '@ais-ui/utils';

const SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000;
const SENTRY_INTERVAL_MS = 30 * 60 * 1000;

function emptyQueryCounts(): MemoryQueryCounts {
  return {
    total: 0,
    active: 0,
    inactive: 0,
    fetching: 0,
    inactiveFetching: 0,
    cachedRecords: 0,
  };
}

function isQueryFamily(value: unknown): value is MemoryQueryFamily {
  return MEMORY_QUERY_FAMILIES.some((family) => family === value);
}

function countCachedRecords(family: MemoryQueryFamily, data: unknown): number {
  if (!data || typeof data !== 'object' || family === 'getOverlay') return 0;
  const field =
    family === 'groundings' || family === 's2sEvents' ? 'items' : 'events';
  if (!(field in data)) return 0;
  const records = (data as Record<string, unknown>)[field];
  return Array.isArray(records) ? records.length : 0;
}

function countDomCanvases(): MemoryResources['domCanvases'] {
  const counts = { count: 0, bytes: 0 };
  document.querySelectorAll('canvas').forEach((canvas) => {
    counts.count++;
    counts.bytes += canvas.width * canvas.height * 4;
  });
  return counts;
}

/** Called only at opt-in capture time; no subscriptions, retained queries or toast arrays. */
export function collectMemoryResources(
  queryCache?: QueryCache,
): MemoryResources {
  const queries = Object.fromEntries(
    MEMORY_QUERY_FAMILIES.map((family) => [family, emptyQueryCounts()]),
  ) as Record<MemoryQueryFamily, MemoryQueryCounts>;
  for (const query of queryCache?.getAll() ?? []) {
    const family = query.queryKey[0];
    if (!isQueryFamily(family)) continue;
    const counts = queries[family];
    const active = query.isActive();
    const fetching = query.state.fetchStatus === 'fetching';
    counts.total++;
    counts.active += Number(active);
    counts.inactive += Number(!active);
    counts.fetching += Number(fetching);
    counts.inactiveFetching += Number(fetching && !active);
    counts.cachedRecords += countCachedRecords(family, query.state.data);
  }
  return {
    visibilityState: document.visibilityState,
    hasFocus: document.hasFocus(),
    queries,
    overlay: memoryDiagnostics.getOverlayCounts(),
    toasts: {
      history: toast.getHistory().length,
      active: toast.getToasts().length,
      rendered: document.querySelectorAll('[data-sonner-toast]').length,
    },
    domCanvases: countDomCanvases(),
  };
}

/** An artifact hash is not a source commit. Never keep a URL, query, fragment or path. */
export function getModuleAssetFingerprint(
  moduleUrl: string,
  development: boolean,
): string {
  if (development) return 'dev:module';
  try {
    const pathname = new URL(moduleUrl).pathname;
    const match = /\/assets\/[^/]+-([A-Za-z0-9_-]{8})\.js$/.exec(pathname);
    return match ? `asset:${match[1]}` : 'unknown:module';
  } catch {
    return 'unknown:module';
  }
}

function getSentryIdentity() {
  const user = Sentry.getIsolationScope().getUser();
  return user?.id || user?.username;
}

/**
 * Reuses the existing diagnostics interval. Bootstrap precedes auth restore,
 * so a pre-auth capture is local only; the first authenticated sample is the
 * heartbeat. Manual captures cannot accelerate the thirty-minute cadence.
 */
export function startMemoryDiagnostics(queryCache?: QueryCache) {
  if (!isMemoryDiagnosticsEnabled()) {
    console.info(
      `[memoryDiagnostics] disabled. To enable: localStorage.setItem('${MEMORY_DIAGNOSTICS_ENABLED_KEY}', '1') then reload.`,
    );
    return;
  }
  let lastReportedAt: number | null = null;
  memoryDiagnostics.start(
    (snapshot) => {
      console.info('[memoryDiagnostics]', memoryDiagnostics.getReport());
      const identity = getSentryIdentity();
      if (!identity || snapshot.authentication !== 'authenticated') return;
      const now = Date.now();
      if (lastReportedAt !== null && now - lastReportedAt < SENTRY_INTERVAL_MS)
        return;
      const sample = lastReportedAt === null ? 'heartbeat' : 'periodic';
      const tags = {
        'memdiag.sample': sample,
        'memdiag.asset': snapshot.assetFingerprint,
        'memdiag.open_olserver_sockets': String(snapshot.openSockets.olserver),
        'memdiag.uptime_hours': (snapshot.uptimeMs / 3_600_000).toFixed(1),
      };
      const resources = snapshot.resources;
      // Flat contexts survive Sentry's default normalization depth, unlike
      // extra.resources.queries.<family>. Keep the local snapshot unchanged.
      const contexts = resources
        ? {
            ...Object.fromEntries(
              MEMORY_QUERY_FAMILIES.map((family) => [
                `memdiag_query_${family}`,
                { ...resources.queries[family] },
              ]),
            ),
            memdiag_overlay: { ...resources.overlay },
            memdiag_toasts: { ...resources.toasts },
            memdiag_dom_canvases: { ...resources.domCanvases },
            memdiag_document: {
              visibilityState: resources.visibilityState,
              hasFocus: resources.hasFocus,
            },
          }
        : {};
      // Scopes merge user/extra dictionaries, so setUser alone would retain
      // isolation-scope session_id. Whitelist after merge for this event only.
      Sentry.withScope((scope) => {
        scope.addEventProcessor((event) => ({
          ...event,
          user: { id: identity },
          extra: { ...snapshot },
          tags,
          contexts,
          breadcrumbs: undefined,
          request: undefined,
        }));
        // Dedupe compares message + fingerprint, ignoring scalar extras.
        // Distinct samples must survive it, while the fingerprint keeps one issue.
        Sentry.captureMessage(
          `Memory diagnostics snapshot [${snapshot.runId}:${snapshot.sequence}]`,
          {
            level: 'info',
            tags,
            extra: { ...snapshot },
            fingerprint: ['memory-diagnostics-snapshot'],
          },
        );
      });
      lastReportedAt = now;
    },
    {
      intervalMs: SNAPSHOT_INTERVAL_MS,
      assetFingerprint: getModuleAssetFingerprint(
        import.meta.url,
        import.meta.env.DEV,
      ),
      getAuthentication: () =>
        getSentryIdentity() ? 'authenticated' : 'pre-auth',
      collectResources: () => collectMemoryResources(queryCache),
    },
  );
  console.info(
    '[memoryDiagnostics] enabled. First authenticated sample is the heartbeat; run memoryDiagnostics.getReport() for local/pre-auth samples.',
  );
  (window as unknown as Record<string, unknown>).memoryDiagnostics =
    memoryDiagnostics;
}
